import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Star, Clock, Camera, RotateCcw, Play, Pause,
  Compass, Download, Info
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

const StarTrailsSimulator = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const [exposureTime, setExposureTime] = useState(60); // minutes
  const [direction, setDirection] = useState(0); // 0 = North (Polaris centered)
  const [focalLength, setFocalLength] = useState(24);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const animationRef = useRef(null);

  // Determine hemisphere
  const isNorthernHemisphere = latitude >= 0;
  
  // Calculate circumpolar angle
  const circumpolarAngle = Math.abs(latitude);
  
  // Calculate trail length in degrees
  const trailLengthDegrees = (exposureTime / 60) * 15; // 15 degrees per hour
  
  // Calculate FOV based on focal length (for 35mm full frame)
  const fovHorizontal = 2 * Math.atan(36 / (2 * focalLength)) * 180 / Math.PI;
  const fovVertical = 2 * Math.atan(24 / (2 * focalLength)) * 180 / Math.PI;

  // Draw star trails
  const drawStarTrails = useCallback((progress = 1) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear with dark sky
    ctx.fillStyle = '#0a0a15';
    ctx.fillRect(0, 0, width, height);
    
    // Draw subtle gradient for sky
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, width/2);
    gradient.addColorStop(0, 'rgba(20, 20, 40, 0.5)');
    gradient.addColorStop(1, 'rgba(5, 5, 15, 0.8)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Calculate pole position based on direction
    const dirRad = direction * Math.PI / 180;
    const poleX = width / 2;
    const poleY = height / 2;
    
    // Stars data - generate random stars
    const stars = [];
    const numStars = 150;
    
    for (let i = 0; i < numStars; i++) {
      // Generate stars at various distances from pole
      const distFromPole = Math.random() * Math.max(width, height) * 0.8;
      const startAngle = Math.random() * Math.PI * 2;
      const brightness = 0.3 + Math.random() * 0.7;
      const size = 0.5 + Math.random() * 1.5;
      
      // Only include stars within FOV
      if (distFromPole < Math.max(width, height) * 0.9) {
        stars.push({
          distFromPole,
          startAngle,
          brightness,
          size
        });
      }
    }
    
    // Draw star trails
    const trailAngle = (trailLengthDegrees * Math.PI / 180) * progress;
    
    stars.forEach(star => {
      const { distFromPole, startAngle, brightness, size } = star;
      
      // Calculate trail
      ctx.beginPath();
      ctx.strokeStyle = `rgba(255, 255, 255, ${brightness * 0.8})`;
      ctx.lineWidth = size;
      ctx.lineCap = 'round';
      
      // Draw arc
      const arcStart = startAngle;
      const arcEnd = startAngle + (isNorthernHemisphere ? -trailAngle : trailAngle);
      
      ctx.arc(poleX, poleY, distFromPole, arcStart, arcEnd, isNorthernHemisphere);
      ctx.stroke();
      
      // Draw bright point at current star position (end of trail)
      const endX = poleX + Math.cos(arcEnd) * distFromPole;
      const endY = poleY + Math.sin(arcEnd) * distFromPole;
      
      ctx.beginPath();
      ctx.fillStyle = `rgba(255, 255, 255, ${brightness})`;
      ctx.arc(endX, endY, size * 1.5, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw pole marker
    ctx.beginPath();
    ctx.strokeStyle = isNorthernHemisphere ? '#ef4444' : '#3b82f6';
    ctx.lineWidth = 2;
    ctx.arc(poleX, poleY, 5, 0, Math.PI * 2);
    ctx.stroke();
    
    // Small cross at pole
    ctx.beginPath();
    ctx.moveTo(poleX - 10, poleY);
    ctx.lineTo(poleX + 10, poleY);
    ctx.moveTo(poleX, poleY - 10);
    ctx.lineTo(poleX, poleY + 10);
    ctx.stroke();
    
    // Label
    ctx.fillStyle = isNorthernHemisphere ? '#ef4444' : '#3b82f6';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(isNorthernHemisphere ? t('startrails.polaris') : t('startrails.southPole'), poleX, poleY - 20);
    
    // Draw compass
    const compassX = width - 40;
    const compassY = 40;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(compassX, compassY, 25, 0, Math.PI * 2);
    ctx.stroke();
    
    const needleAngle = (-direction) * Math.PI / 180;
    ctx.fillStyle = '#ef4444';
    ctx.beginPath();
    ctx.moveTo(compassX + Math.sin(needleAngle) * 18, compassY - Math.cos(needleAngle) * 18);
    ctx.lineTo(compassX + Math.sin(needleAngle + 2.8) * 5, compassY - Math.cos(needleAngle + 2.8) * 5);
    ctx.lineTo(compassX + Math.sin(needleAngle - 2.8) * 5, compassY - Math.cos(needleAngle - 2.8) * 5);
    ctx.closePath();
    ctx.fill();
    
    ctx.fillStyle = 'white';
    ctx.font = 'bold 10px sans-serif';
    ctx.fillText('N', compassX, compassY - 30);
    
    // Info overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(10, height - 50, 180, 40);
    ctx.fillStyle = 'white';
    ctx.font = '11px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`${t('startrails.canvasTrail')} ${(trailLengthDegrees * progress).toFixed(1)}° / ${exposureTime}min`, 18, height - 32);
    ctx.fillText(`FOV: ${fovHorizontal.toFixed(0)}° x ${fovVertical.toFixed(0)}°`, 18, height - 17);
    
  }, [direction, exposureTime, focalLength, trailLengthDegrees, fovHorizontal, fovVertical, isNorthernHemisphere, t]);

  // Animation loop
  useEffect(() => {
    if (isAnimating) {
      const startTime = Date.now();
      const duration = 5000; // 5 second animation
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setAnimationProgress(progress);
        drawStarTrails(progress);
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating, drawStarTrails]);

  // Redraw when parameters change
  useEffect(() => {
    if (!isAnimating) {
      drawStarTrails(1);
    }
  }, [direction, exposureTime, focalLength, drawStarTrails, isAnimating]);

  // Calculate recommended exposure for desired trail length
  const calculateExposureForTrail = (desiredDegrees) => {
    return Math.round((desiredDegrees / 15) * 60);
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400" />
              {t('startrails.title')}
            </CardTitle>
            <Badge className={isNorthernHemisphere ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'}>
              {isNorthernHemisphere ? t('startrails.northernHemisphere') : t('startrails.southernHemisphere')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Canvas */}
          <div className="relative rounded-lg overflow-hidden border border-white/10">
            <canvas
              ref={canvasRef}
              width={400}
              height={280}
              className="w-full"
            />
          </div>

          {/* Controls */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t('startrails.exposure')} {exposureTime} min ({(exposureTime/60).toFixed(1)}h)
              </Label>
              <Slider
                value={[exposureTime]}
                onValueChange={([v]) => setExposureTime(v)}
                min={5}
                max={480}
                step={5}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Compass className="w-3 h-3" />
                {t('startrails.cameraDirection')} {direction}°
              </Label>
              <Slider
                value={[direction]}
                onValueChange={([v]) => setDirection(v)}
                min={0}
                max={360}
                step={5}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Camera className="w-3 h-3" />
                {t('startrails.focalLength')} {focalLength}mm
              </Label>
              <Slider
                value={[focalLength]}
                onValueChange={([v]) => setFocalLength(v)}
                min={8}
                max={200}
                step={1}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400">{t('startrails.trailLength')}</Label>
              <div className="text-xl font-mono font-bold text-amber-400">
                {trailLengthDegrees.toFixed(1)}°
              </div>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500">{t('startrails.quick')}</span>
            {[
              { label: '15° trail', labelKey: 'startrails.preset15', exp: calculateExposureForTrail(15) },
              { label: '45° trail', labelKey: 'startrails.preset45', exp: calculateExposureForTrail(45) },
              { label: '90° trail', labelKey: 'startrails.preset90', exp: calculateExposureForTrail(90) },
              { label: 'Full circle', labelKey: 'startrails.presetFullCircle', exp: calculateExposureForTrail(360) },
            ].map(preset => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setExposureTime(preset.exp)}
              >
                {t(preset.labelKey)}
              </Button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAnimationProgress(0);
                setIsAnimating(true);
              }}
              className="flex-1 border-white/10"
              disabled={isAnimating}
            >
              {isAnimating ? (
                <>
                  <Pause className="w-4 h-4 mr-1" />
                  {t('startrails.animating')}
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  {t('startrails.animateTrails')}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDirection(0)}
              className="border-white/10"
            >
              <Compass className="w-4 h-4 mr-1" />
              {t('startrails.north')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setExposureTime(60);
                setDirection(0);
                setFocalLength(24);
              }}
              className="border-white/10"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calculations */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Info className="w-4 h-4 text-cyan-400" />
            {t('startrails.trailCalculations')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">{t('startrails.trailArc')}</p>
              <p className="font-mono text-lg text-white">{trailLengthDegrees.toFixed(1)}°</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">{t('startrails.totalTime')}</p>
              <p className="font-mono text-lg text-white">{exposureTime} min</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">FOV</p>
              <p className="font-mono text-lg text-white">{fovHorizontal.toFixed(0)}°</p>
            </div>
          </div>

          <div className="mt-3 p-2 bg-slate-800/30 rounded text-xs text-slate-400">
            <p className="mb-1">📐 <strong>{t('startrails.formulaLabel')}</strong> Trail (°) = Exposure (h) × 15°/h</p>
            <p>⏱️ <strong>{t('startrails.fullCircleLabel')}</strong> {calculateExposureForTrail(360)} min = {(calculateExposureForTrail(360)/60).toFixed(1)} {t('startrails.hours')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading">📸 {t('startrails.tips')}</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400 space-y-2">
          <p>• <strong>{t('startrails.circumpolar')}</strong> {t('startrails.pointAt')} {isNorthernHemisphere ? t('startrails.polarisNorth') : t('startrails.southCelestialPole')} {t('startrails.forCircularTrails')}</p>
          <p>• <strong>{t('startrails.equatorial')}</strong> {t('startrails.tipEquatorialText')}</p>
          <p>• <strong>{t('startrails.stacking')}</strong> {t('startrails.tipStackingText')}</p>
          <p>• <strong>{t('startrails.gapFree')}</strong> {t('startrails.tipGapFreeText')}</p>
          <p>• <strong>{t('startrails.yourLatitude')}</strong> {Math.abs(latitude).toFixed(1)}° {latitude >= 0 ? 'N' : 'S'} {t('startrails.circumpolarStarsWithin')} {circumpolarAngle.toFixed(0)}° {t('startrails.ofPole')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StarTrailsSimulator;
